import type { BoardCard } from '$lib/types';

export function boardSourceLabel(card: Pick<BoardCard, 'source' | 'created_by_label'>, commandCentre: string, omar: string): string {
  // Intake cards may still carry the legacy issuer title; the source wins.
  if (card.source === 'chief_of_staff') return omar;
  return card.created_by_label?.trim() || (card.source === 'command_centre' ? commandCentre : card.source || '');
}

// Older cards store an issuer label rather than a creator ID. Only apply the
// self-authored wording to the viewer's own card and an exact display-name
// match; never infer ownership from a label on someone else's board.
export function boardIsMyTask(card: Pick<BoardCard, 'user_id' | 'source' | 'created_by_label'>, user: { id: number; name: string } | null): boolean {
  if (!user || card.user_id !== user.id) return false;
  const source = card.source?.trim();
  if (source === 'chief_of_staff') return false;
  const label = card.created_by_label?.trim();
  if (!source && !label) return true;
  return Boolean(label && user.name.trim() && label.normalize('NFKC').toLocaleLowerCase() === user.name.trim().normalize('NFKC').toLocaleLowerCase());
}

// Compare calendar dates in the app's business timezone, not UTC instants.
export function boardToday(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}

export function boardCheckInDismissalKey(userId: number | undefined): string {
  return `board-checkin-dismissed:${userId ?? 'self'}`;
}

export function boardPostCheckInVisible(completedFor: number | null, currentUserId: number | undefined, dismissedDay: string, today = boardToday()): boolean {
  return completedFor !== null && completedFor === currentUserId && dismissedDay !== today;
}

export function boardDueState(card: Pick<BoardCard, 'due_date' | 'col'>): 'overdue' | 'today' | 'upcoming' | null {
  if (!card.due_date || card.col === 'done') return null;
  const date = card.due_date.slice(0, 10);
  const today = boardToday();
  return date < today ? 'overdue' : date === today ? 'today' : 'upcoming';
}

export function boardDate(date: string): string {
  return new Date(`${date.slice(0, 10)}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function boardRelativeDate(date: string, locale: string, now = Date.now()): string {
  const seconds = (new Date(date).getTime() - now) / 1000;
  const magnitude = Math.abs(seconds);
  const [unit, divisor]: [Intl.RelativeTimeFormatUnit, number] = magnitude < 60 ? ['second', 1]
    : magnitude < 3600 ? ['minute', 60]
    : magnitude < 86400 ? ['hour', 3600]
    : magnitude < 30 * 86400 ? ['day', 86400]
    : magnitude < 365 * 86400 ? ['month', 30 * 86400]
    : ['year', 365 * 86400];
  return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(Math.trunc(seconds / divisor), unit);
}

export function boardReminders(cards: BoardCard[], today = boardToday(), now = Date.now()): BoardCard[] {
  const overdue = (card: BoardCard) => Boolean(card.due_date && card.due_date.slice(0, 10) < today);
  const rank = (card: BoardCard) => overdue(card) ? 0 : card.col === 'blocked' ? 1 : 2;
  return cards.filter((card) => card.col !== 'done' && (card.source === 'chief_of_staff' || overdue(card) ||
    card.col === 'blocked' || now - new Date(card.updated_at).getTime() > 7 * 86400000))
    .sort((a, b) => rank(a) - rank(b) ||
      (overdue(a) && overdue(b) ? a.due_date!.localeCompare(b.due_date!) : 0) ||
      a.updated_at.localeCompare(b.updated_at) || a.id - b.id)
    .slice(0, 5);
}

export type BoardDateShortcut = 'today' | 'tomorrow' | 'friday' | 'monday';
export function boardShortcutDate(shortcut: BoardDateShortcut, today = boardToday()): string {
  const date = new Date(`${today}T12:00:00Z`);
  const day = date.getUTCDay();
  const offset = shortcut === 'today' ? 0 : shortcut === 'tomorrow' ? 1 : shortcut === 'friday' ? (5 - day + 7) % 7 : (1 - day + 7) % 7 || 7;
  date.setUTCDate(date.getUTCDate() + offset);
  return date.toISOString().slice(0, 10);
}
